/**
 * Where the money came from — the part of Earnings that isn't the shop.
 *
 * Shown above the shop figures and built from whatever a person actually runs: sessions, events,
 * workshops, streams and the shop each get their own line, and each line says where the money IS.
 * Shop takings are collected by the house and transferred later; session and event takings are
 * already in their own Stripe. One total across both would read as a single pot, which it is not.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, ExternalLink, Info, Wallet, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { apiFetch } from "../../pages/marketplace/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DETAIL_TAB = {
  "workshop-earnings": "Workshop Earnings",
  orders: "Orders",
  events: "Events",
};

export const EarningsBySource = () => {
  const navigate = useNavigate();
  // His choice: a line in Earnings that OPENS the detailed page, rather than replacing it.
  const openTab = (tab) => navigate(`/dashboard/practitioner-hub?tab=${tab}`);
  const [data, setData] = useState(null);
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [taxBusy, setTaxBusy] = useState(false);
  const [openSource, setOpenSource] = useState(null);

  const load = useCallback(async () => {
    const qs = new URLSearchParams();
    if (since) qs.set("since", since);
    if (until) qs.set("until", until);
    try {
      setData(await apiFetch(`/practitioner/earnings/overview?${qs.toString()}`));
    } catch {
      setData({ lines: [], totals: {} });
    }
  }, [since, until]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    const qs = new URLSearchParams();
    if (since) qs.set("since", since);
    if (until) qs.set("until", until);
    const res = await fetch(`${API}/practitioner/earnings/export.csv?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `earnings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openTaxDocuments = async () => {
    setTaxBusy(true);
    try {
      const r = await apiFetch("/vendor/stripe/tax-documents-link", { method: "POST" });
      if (r?.url) window.open(r.url, "_blank", "noopener");
    } catch (e) {
      alert(e?.message || "Complete your Stripe onboarding first.");
    }
    setTaxBusy(false);
  };

  if (!data) return null;
  const lines = data.lines || [];
  const totals = data.totals || {};

  return (
    <div className="ve-source-section" data-testid="earnings-by-source">
      <style>{`
        .ve-source-section {
          background: #fff; border: 1px solid #e4e0d8; border-radius: 12px;
          padding: 18px 20px; margin: 0 28px 18px;
        }
        .ve-source-head {
          display: flex; justify-content: space-between; align-items: flex-end;
          gap: 12px; flex-wrap: wrap; margin-bottom: 14px;
        }
        .ve-source-title {
          font-family: 'Literata', serif; font-size: 17px; color: #1a1a16; margin: 0;
        }
        .ve-source-sub { font-size: 12px; color: #8a8a80; margin: 3px 0 0; }
        .ve-range { display: flex; gap: 8px; align-items: flex-end; flex-wrap: wrap; }
        .ve-range label { font-size: 11px; color: #5a5a54; display: block; margin-bottom: 3px; }
        .ve-range input {
          font-size: 13px; padding: 6px 8px; border: 1px solid #e4e0d8; border-radius: 7px;
          color: #1a1a16; background: #fff;
        }
        .ve-src-btn {
          font-size: 12px; font-weight: 600; padding: 7px 11px; border-radius: 7px;
          border: 1px solid #e4e0d8; background: #fff; color: #5a5a54; cursor: pointer;
          display: inline-flex; align-items: center; gap: 5px;
        }
        .ve-src-btn:hover { border-color: #c09840; color: #1a1a16; }
        .ve-src-row {
          display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
          border-top: 1px solid #f0ece4; padding: 10px 0; font-size: 14px; color: #1a1a16;
        }
        .ve-src-row:first-of-type { border-top: none; }
        .ve-src-name { flex: 1; min-width: 160px; font-weight: 600; }
        .ve-src-meta { font-size: 12px; color: #8a8a80; font-weight: 400; }
        .ve-src-where {
          font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px;
          padding: 3px 8px; border-radius: 10px; border: 1px solid;
        }
        .ve-src-total {
          display: flex; gap: 22px; flex-wrap: wrap; border-top: 2px solid #efeae2;
          margin-top: 10px; padding-top: 12px; font-size: 13px; color: #5a5a54;
        }
        .ve-src-total strong { display: block; font-size: 19px; color: #1a1a16; }
        .ve-txns {
          margin: 2px 0 10px; border-left: 2px solid #efeae2; padding-left: 12px;
        }
        .ve-txn {
          display: flex; gap: 10px; flex-wrap: wrap; align-items: baseline;
          font-size: 13px; color: #5a5a54; padding: 4px 0;
        }
        .ve-txn-date { min-width: 86px; color: #8a8a80; font-variant-numeric: tabular-nums; }
        .ve-txn-what { flex: 1; min-width: 180px; color: #1a1a16; }
        .ve-txn-fig { min-width: 76px; text-align: right; font-variant-numeric: tabular-nums; }
        .ve-txn-head { font-weight: 600; color: #1a1a16; border-bottom: 1px solid #f0ece4; }
        .ve-tax-note {
          margin-top: 14px; font-size: 12px; line-height: 1.6; color: #5a5a54;
          background: #faf7f2; border: 1px solid #efeae2; border-radius: 9px; padding: 11px 13px;
        }
      `}</style>

      <div className="ve-source-head">
        <div>
          <p className="ve-source-title">Where it came from</p>
          <p className="ve-source-sub">
              Only the things you run appear here, payment by payment. Gifts from clients are
            listed separately below.
          </p>
        </div>
        <div className="ve-range">
          <div>
            <label htmlFor="ve-since">From</label>
            <input id="ve-since" type="date" value={since} data-testid="earnings-since"
              onChange={(e) => setSince(e.target.value)} />
          </div>
          <div>
            <label htmlFor="ve-until">To</label>
            <input id="ve-until" type="date" value={until} data-testid="earnings-until"
              onChange={(e) => setUntil(e.target.value)} />
          </div>
          <button className="ve-src-btn" onClick={download} data-testid="earnings-download">
            <Download size={13} /> Download
          </button>
        </div>
      </div>

      {lines.length === 0 ? (
        <p style={{ fontSize: 13, color: "#8a8a80", margin: 0 }} data-testid="earnings-no-sources">
          Nothing has come in yet in this period. Sessions, events, workshops and shop sales will
          each appear here as their own line.
        </p>
      ) : (
        <>
          {lines.map((line) => (
            <div key={line.key}>
            <div className="ve-src-row" data-testid={`earnings-line-${line.key}`}>
              <span className="ve-src-name">
                {line.label}
                <span className="ve-src-meta">
                  {" "}· {line.count} payment{line.count === 1 ? "" : "s"}
                  {line.fees ? ` · $${line.fees.toFixed(2)} in fees` : ""}
                  {line.tax ? ` · $${line.tax.toFixed(2)} tax collected` : ""}
                </span>
              </span>
              <span className="ve-src-where" style={line.where === "in_stripe"
                ? { background: "#eaf5f0", color: "#1a5a44", borderColor: "#a0d8c4" }
                : { background: "#fdf6e3", color: "#7a5200", borderColor: "#efdca8" }}>
                {line.where === "in_stripe"
                  ? <><Wallet size={10} style={{ display: "inline", marginRight: 4 }} />In your Stripe</>
                  : <><Clock size={10} style={{ display: "inline", marginRight: 4 }} />Owed to you</>}
              </span>
              <strong style={{ minWidth: 92, textAlign: "right" }}>
                ${line.net.toFixed(2)}
              </strong>
              <button className="ve-src-btn"
                onClick={() => setOpenSource(openSource === line.key ? null : line.key)}
                data-testid={`earnings-expand-${line.key}`}
                title="Every payment behind this figure">
                {openSource === line.key ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                {line.count}
              </button>
              {line.detail_tab && (
                <button className="ve-src-btn" onClick={() => openTab(line.detail_tab)}
                  data-testid={`earnings-detail-${line.key}`}>
                  {DETAIL_TAB[line.detail_tab] || "Details"}
                </button>
              )}
            </div>
            {openSource === line.key && (
              <div className="ve-txns" data-testid={`earnings-txns-${line.key}`}>
                <div className="ve-txn ve-txn-head">
                  <span className="ve-txn-date">Date</span>
                  <span className="ve-txn-what">What it was for</span>
                  <span className="ve-txn-fig">Gross</span>
                  <span className="ve-txn-fig">Fee</span>
                  <span className="ve-txn-fig">Tax</span>
                  <span className="ve-txn-fig">Net</span>
                </div>
                {(data.transactions || [])
                  .filter((t) => t.source === line.key)
                  .map((t, i) => (
                    <div className="ve-txn" key={`${t.reference}-${i}`}
                      data-testid={`earnings-txn-${line.key}-${i}`}>
                      <span className="ve-txn-date">{t.date}</span>
                      <span className="ve-txn-what">{t.description}</span>
                      <span className="ve-txn-fig">${t.gross.toFixed(2)}</span>
                      <span className="ve-txn-fig">${t.fee.toFixed(2)}</span>
                      <span className="ve-txn-fig">${t.tax.toFixed(2)}</span>
                      <span className="ve-txn-fig" style={{ color: "#1a1a16", fontWeight: 600 }}>
                        ${t.net.toFixed(2)}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
          ))}

          <div className="ve-src-total">
            <span>Net in this period<strong data-testid="earnings-total-net">
              ${(totals.net || 0).toFixed(2)}</strong></span>
            <span>Already in your Stripe<strong data-testid="earnings-total-stripe">
              ${(totals.in_stripe || 0).toFixed(2)}</strong></span>
            <span>Still owed to you<strong data-testid="earnings-total-owed">
              ${(totals.owed_to_you || 0).toFixed(2)}</strong></span>
            <span>Tax collected<strong>${(totals.tax || 0).toFixed(2)}</strong></span>
            {data.owed_to_platform > 0 && (
              <span>You owe the platform<strong data-testid="earnings-owed-platform">
                ${data.owed_to_platform.toFixed(2)}</strong></span>
            )}
          </div>
        </>
      )}

      <div className="ve-tax-note" data-testid="earnings-tax-note">
        <Info size={12} style={{ display: "inline", marginRight: 5 }} />
        {data.tax_note}
        <br />
        <button className="ve-src-btn" style={{ marginTop: 9 }} onClick={openTaxDocuments}
          disabled={taxBusy} data-testid="earnings-tax-documents">
          <ExternalLink size={12} /> {taxBusy ? "Opening…" : "View my Stripe tax documents"}
        </button>
      </div>
    </div>
  );
};
